
import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

interface PresupuestoModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const PresupuestoModal = ({ isOpen, onClose }: PresupuestoModalProps) => {
  const [formData, setFormData] = useState({
    nombre: '',
    telefono: '',
    email: '',
    tipoInmueble: '',
    metrosCuadrados: '',
    direccion: '',
    plantas: '',
    ascensor: 'si',
    urgencia: false,
    observaciones: '',
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    await new Promise((resolve) => setTimeout(resolve, 1500));

    setIsSubmitting(false);
    setSubmitSuccess(true);

    setTimeout(() => {
      setSubmitSuccess(false);
      onClose();
      setFormData({
        nombre: '',
        telefono: '',
        email: '',
        tipoInmueble: '',
        metrosCuadrados: '',
        direccion: '',
        plantas: '',
        ascensor: 'si',
        urgencia: false,
        observaciones: '',
      });
    }, 2000);
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value, type } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value,
    }));
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={onClose}
          />

          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className="relative bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
          >
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between z-10">
              <h3 className="text-2xl font-bold text-navy">Solicitar presupuesto</h3>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 text-2xl cursor-pointer"
                aria-label="Cerrar"
              >
                <i className="ri-close-line"></i>
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6">
              {submitSuccess ? (
                <div className="text-center py-12">
                  <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <i className="ri-check-line text-4xl text-green-600"></i>
                  </div>
                  <h4 className="text-2xl font-bold text-navy mb-2">¡Solicitud enviada!</h4>
                  <p className="text-gray-600">
                    Te respondemos en cuanto lo veamos. Gracias por confiar en nosotros.
                  </p>
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                    <div>
                      <label className="block text-sm font-medium text-navy mb-2">
                        Nombre completo *
                      </label>
                      <input
                        type="text"
                        name="nombre"
                        value={formData.nombre}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:border-orange focus:ring-2 focus:ring-orange/20"
                        placeholder="Tu nombre"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-navy mb-2">
                        Teléfono *
                      </label>
                      <input
                        type="tel"
                        name="telefono"
                        value={formData.telefono}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:border-orange focus:ring-2 focus:ring-orange/20"
                        placeholder="+34 600 000 000"
                      />
                    </div>
                  </div>

                  <div className="mb-6">
                    <label className="block text-sm font-medium text-navy mb-2">Email *</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:border-orange focus:ring-2 focus:ring-orange/20"
                      placeholder="tu@email.com"
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                    <div>
                      <label className="block text-sm font-medium text-navy mb-2">
                        Tipo de inmueble *
                      </label>
                      <select
                        name="tipoInmueble"
                        value={formData.tipoInmueble}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:border-orange focus:ring-2 focus:ring-orange/20 cursor-pointer"
                      >
                        <option value="">Selecciona...</option>
                        <option value="piso">Piso</option>
                        <option value="casa">Casa</option>
                        <option value="local">Local comercial</option>
                        <option value="trastero">Trastero</option>
                        <option value="oficina">Oficina</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-navy mb-2">
                        Metros cuadrados aprox.
                      </label>
                      <div className="relative">
                        <input
                          type="number"
                          name="metrosCuadrados"
                          value={formData.metrosCuadrados}
                          onChange={handleChange}
                          className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:border-orange focus:ring-2 focus:ring-orange/20"
                          placeholder="80"
                        />
                        <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 text-sm">
                          m²
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="mb-6">
                    <label className="block text-sm font-medium text-navy mb-2">
                      Dirección del inmueble
                    </label>
                    <input
                      type="text"
                      name="direccion"
                      value={formData.direccion}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:border-orange focus:ring-2 focus:ring-orange/20"
                      placeholder="Calle, número, Barcelona"
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                    <div>
                      <label className="block text-sm font-medium text-navy mb-2">
                        ¿En qué planta?
                      </label>
                      <input
                        type="text"
                        name="plantas"
                        value={formData.plantas}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:border-orange focus:ring-2 focus:ring-orange/20"
                        placeholder="Ej: 3º"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-navy mb-2">
                        ¿Hay ascensor?
                      </label>
                      <select
                        name="ascensor"
                        value={formData.ascensor}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:border-orange focus:ring-2 focus:ring-orange/20 cursor-pointer"
                      >
                        <option value="si">Sí</option>
                        <option value="no">No</option>
                      </select>
                    </div>
                  </div>

                  <div className="mb-6">
                    <label className="flex items-center gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        name="urgencia"
                        checked={formData.urgencia}
                        onChange={handleChange}
                        className="w-5 h-5 text-orange border-gray-300 rounded focus:ring-orange cursor-pointer"
                      />
                      <span className="text-sm font-medium text-navy">
                        Es urgente (necesito el servicio en menos de 48h)
                      </span>
                    </label>
                  </div>

                  <div className="mb-6">
                    <label className="block text-sm font-medium text-navy mb-2">
                      Observaciones adicionales
                    </label>
                    <textarea
                      name="observaciones"
                      value={formData.observaciones}
                      onChange={handleChange}
                      maxLength={500}
                      rows={4}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:border-orange focus:ring-2 focus:ring-orange/20 resize-y"
                      placeholder="Cuéntanos cualquier detalle que consideres importante..."
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Máximo 500 caracteres ({formData.observaciones.length}/500)
                    </p>
                  </div>

                  <div className="bg-gray-50 rounded-xl p-4 mb-6 flex items-start gap-3">
                    <i className="ri-lock-line text-gray-600 text-lg mt-0.5"></i>
                    <p className="text-xs text-gray-600 leading-relaxed">
                      No compartimos tus datos. Los utilizamos únicamente para preparar tu presupuesto y contactarte. Consulta nuestra{' '}
                      <a href="#" className="text-orange hover:underline cursor-pointer">
                        política de privacidad
                      </a>
                      .
                    </p>
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-orange text-white py-4 rounded-xl font-medium hover:bg-orange-light hover:scale-[1.02] transition-all disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
                  >
                    {isSubmitting ? (
                      <span className="flex items-center justify-center gap-2">
                        <i className="ri-loader-4-line animate-spin"></i>
                        Enviando...
                      </span>
                    ) : (
                      'Enviar solicitud'
                    )}
                  </button>
                </>
              )}
            </form>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default PresupuestoModal;
